"use client";

/**
 * The next/link is poorly designed; as it makes a network request on all links simultaneously,
 * which DDOS the server and consumes the user device CPU over 100%.
 * This version defers the requests to be run serially to prevent the spam and prevents CPU overload.
 *
 * On mobile/tablets, all links are prefetch serially (since there's no hover state) when they come in view.
 * On desktop, links are prefetch on hover.
 *
 * From:
 * https://gist.github.com/khuezy/34fd7fe245018887f86691463262dcba
 */

import {
  useCallback,
  useEffect,
  useRef,
  type MouseEvent,
  type AnchorHTMLAttributes,
} from "react";
import { useRouter } from "next/navigation";
import { useOnScreen, hasTouch } from "~/utils/next/link";
import { type AppRouterInstance } from "next/dist/shared/lib/app-router-context";

const queue: string[] = [];
const fetched = new Set<string>();

let router: AppRouterInstance;

// A global generator to queue up fetch requests
async function* prefetch(): AsyncGenerator<void, void, void> {
  while (true) {
    while (queue.length) {
      // eslint-disable-next-line @typescript-eslint/await-thenable
      await router.prefetch(queue.shift()!);
    }
    yield;
  }
}
const generator = prefetch();

// For phones and tablets, prefetch all links in view
function TouchLink(props: AnchorHTMLAttributes<HTMLAnchorElement>) {
  router = useRouter();
  const href = props.href ?? "";

  const ref = useRef<HTMLAnchorElement>(null);
  const onScreen = useOnScreen(ref);

  // Queue up prefetch
  useEffect(() => {
    if (!onScreen || fetched.has(href)) return;
    queue.push(href);
    void generator.next();
    fetched.add(href);
  }, [href, onScreen]);

  const onClick = useCallback(
    function onClick(e: MouseEvent<HTMLAnchorElement>) {
      e.preventDefault();
      props?.onClick?.(e);
      router.push(href);
    },
    [href],
  );

  return <a ref={ref} {...props} onClick={onClick} />;
}

// For nonphone/tablet devices, only prefetch on link hover
function DesktopLink({
  scrolling,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> & { scrolling?: boolean }) {
  router = useRouter();
  const href = props.href ?? "";
  const onClick = useCallback(
    function onClick(e: MouseEvent<HTMLAnchorElement>) {
      e.preventDefault();
      props?.onClick?.(e);
      router.push(href, {});
    },
    [href],
  );

  const onHover = useCallback(
    function onHover(e: MouseEvent<HTMLAnchorElement>) {
      e.preventDefault();
      router.prefetch(href);
    },
    [href],
  );
  return (
    <a
      {...props}
      onClick={onClick}
      onMouseEnter={scrolling ? undefined : onHover}
    />
  );
}

export default function Link(
  props: AnchorHTMLAttributes<HTMLAnchorElement> & { scrolling?: boolean },
) {
  return hasTouch() ? <TouchLink {...props} /> : <DesktopLink {...props} />;
}
